import { DataTable, ROW_CLASS, TOTAL_ROW_CLASS } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { COLUMN_HELP } from "@/lib/column-help";
import { money } from "@/lib/format";
import type { AccountPnlLine, PnlStatement } from "@/lib/profitability-api";
import { cn } from "@/lib/utils";

/**
 * NOS's profit-and-loss statement for one SUBJECT -- an account or a demand --
 * in NOS's own shape: lines down, years across, subtotals in bold.
 *
 * One table for both, because it is one statement: the project endpoint returns
 * the same lines, the same NOS-computed subtotals and the same
 * years-are-calendar-years split, and a second copy of this file would be a
 * second place for the subtotal rule below to be got wrong. It takes
 * `PnlStatement` -- years and lines, nothing else -- so neither page can hand
 * it the other's subject id.
 *
 * Two rules the numbers depend on, both easy to break by accident:
 *
 * 1. THE SUBTOTALS ARE DATA. Total Revenue, Total Cost and Net Profit arrive as
 *    lines with `is_summary` set. Nothing here adds them up, and nothing here
 *    folds them in with the detail lines -- summing the whole array gives twice
 *    the revenue, twice the cost and the net profit on top of both.
 * 2. THE YEAR COLUMNS ARE NOT RANGE-SCOPED. `by_year` is full calendar years,
 *    every year the account has traded. Only `range_total` answers "and over
 *    the months I selected", which is why it gets its own column beside them
 *    rather than being left for the reader to infer from the years.
 *
 * The rows are deliberately not sortable. Elsewhere in this app every column
 * sorts; here the order IS the statement -- revenue, its total, cost, its
 * total, the profit that falls out -- and re-ordering it would leave a subtotal
 * sitting above the lines it totals.
 */

/** A P&L cell. A year the line never appeared in is absent from `by_year`;
 *  that is "no such cost this year", which is not the same claim as $0. */
function PnlCell({ value, line, year }: { value: number | undefined; line: string; year: string }) {
  if (value === undefined) {
    return (
      <span className="text-muted-foreground" title={`No ${line} booked in ${year}`}>
        —
      </span>
    );
  }
  return (
    <span className={cn("tabular-nums", value < 0 && "text-destructive")}>{money(value)}</span>
  );
}

function PnlRow({ line, years }: { line: AccountPnlLine; years: string[] }) {
  // The subtotal's weight and rule are what make the statement readable at a
  // glance; the indent on everything else is what makes them read as the lines
  // being totalled rather than as three more lines.
  const summary = line.is_summary;
  return (
    <tr className={cn(ROW_CLASS, summary && TOTAL_ROW_CLASS)}>
      <th
        scope="row"
        className={cn(
          "px-5 py-2.5 text-left font-normal",
          summary ? "font-semibold" : "pl-8 text-muted-foreground",
        )}
      >
        {line.line}
      </th>
      {years.map((year) => (
        <td
          key={year}
          className={cn("px-5 py-2.5 text-right", summary && "font-semibold text-foreground")}
        >
          <PnlCell value={line.by_year[year]} line={line.line} year={year} />
        </td>
      ))}
      {/* The range column is a different scope from the years beside it, so it
          gets a rule of its own rather than reading as one more year. */}
      <td
        className={cn(
          "border-l border-border px-5 py-2.5 text-right",
          summary && "font-semibold text-foreground",
        )}
      >
        <span className={cn("tabular-nums", line.range_total < 0 && "text-destructive")}>
          {money(line.range_total)}
        </span>
      </td>
    </tr>
  );
}

export function PnlStatementTable({
  pnl,
  rangeLabel,
  emptyDescription = "NOS has no profit-and-loss lines for this account.",
}: {
  pnl: PnlStatement;
  rangeLabel: string;
  /** What "no lines" means for THIS subject; the default names an account. */
  emptyDescription?: string;
}) {
  // NOS's own ordering, applied here rather than trusted from the response --
  // the statement is meaningless if Total Cost lands above Employee Cost, and
  // "the API returned them in order" is the assumption that broke the period
  // charts on this same page twice.
  const lines = [...pnl.lines].sort((a, b) => a.sort_index - b.sort_index);

  return (
    <DataTable
      flush
      headers={[
        { label: "Line", help: COLUMN_HELP.pnlLine },
        ...pnl.years.map((year) => ({
          label: year,
          className: "text-right",
          help: COLUMN_HELP.pnlYear,
        })),
        {
          label: rangeLabel || "Selected range",
          className: "border-l border-border text-right",
          help: COLUMN_HELP.pnlRange,
        },
      ]}
      empty={<EmptyState title="No P&L on record" description={emptyDescription} />}
    >
      {lines.map((line) => (
        <PnlRow key={line.enum || line.sort_index} line={line} years={pnl.years} />
      ))}
    </DataTable>
  );
}

/**
 * The account page's name for the same table, kept so its call site reads as
 * what it is. Structurally `AccountPnl` still satisfies the `pnl` prop, so
 * nothing about that page's usage changes.
 */
export const AccountPnl = PnlStatementTable;
